/**
 * Dataverse Web API Client
 *
 * Type-safe client for interacting with the Dataverse Web API.
 * Handles authentication, request building, and error handling.
 */

import { env } from '@shared/config';
import type {
  ODataCollectionResponse,
  ODataError,
  QueryOptions,
  MutationOptions,
  TableName,
} from './types';
import { buildQueryString } from './utils';

// =============================================================================
// Types
// =============================================================================

/**
 * Error thrown by Dataverse operations
 */
export class DataverseError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly innerError?: ODataError['error']['innererror'];

  constructor(message: string, code: string, status: number, innerError?: ODataError['error']['innererror']) {
    super(message);
    this.name = 'DataverseError';
    this.code = code;
    this.status = status;
    this.innerError = innerError;
  }

  static fromResponse(status: number, error: ODataError): DataverseError {
    return new DataverseError(
      error.error.message,
      error.error.code,
      status,
      error.error.innererror
    );
  }
}

/**
 * Token provider function type
 */
type TokenProvider = () => Promise<string>;

// =============================================================================
// Client Configuration
// =============================================================================

interface ClientConfig {
  baseUrl: string;
  apiVersion: string;
  getToken: TokenProvider;
}

let clientConfig: ClientConfig | null = null;

// =============================================================================
// Impersonation
// =============================================================================

/** Azure AD object ID of the user to impersonate, or null for normal operation. */
let impersonateAsOid: string | null = null;

/**
 * Set or clear user impersonation. When set, all Dataverse requests include
 * the CallerObjectId header so Dataverse executes them as the target user.
 */
export function setImpersonation(oid: string | null): void {
  impersonateAsOid = oid;
}

/** Returns the current impersonation OID, or null. */
export function getImpersonation(): string | null {
  return impersonateAsOid;
}

/**
 * Listeners notified the first time the client becomes initialized. Used by
 * fire-and-forget emitters (e.g. user-activity tracker) that may be invoked
 * before DataverseOrgProvider's init effect has run.
 */
type ReadyListener = () => void;
const readyListeners = new Set<ReadyListener>();

function notifyReady(): void {
  for (const cb of [...readyListeners]) {
    try {
      cb();
    } catch {
      /* ignore listener errors — telemetry must never break the app */
    }
  }
}

/** Whether the Dataverse client has been initialized at least once. */
export function isDataverseClientInitialized(): boolean {
  return clientConfig !== null;
}

/**
 * Subscribe to client-ready notifications. The listener fires every time
 * the client is (re)initialized; if the client is already ready, the
 * listener is invoked immediately.
 */
export function onDataverseClientReady(cb: ReadyListener): () => void {
  readyListeners.add(cb);
  if (clientConfig) cb();
  return () => readyListeners.delete(cb);
}

/**
 * Initialize the Dataverse client
 * Must be called before any API operations
 */
export function initializeDataverseClient(getToken: TokenProvider): void {
  clientConfig = {
    baseUrl: env.dataverse.url,
    apiVersion: env.dataverse.apiVersion,
    getToken,
  };
  notifyReady();
}

/**
 * Reinitialize the Dataverse client with a different base URL and token provider.
 * Used by Vera Sales org switcher to toggle between dev/prod Dataverse orgs at runtime.
 */
export function reinitializeDataverseClient(baseUrl: string, getToken: TokenProvider): void {
  clientConfig = {
    baseUrl,
    apiVersion: env.dataverse.apiVersion,
    getToken,
  };
  notifyReady();
}

/**
 * Get the API base URL
 */
function getApiUrl(): string {
  if (!clientConfig) {
    throw new Error('Dataverse client not initialized. Call initializeDataverseClient first.');
  }
  return `${clientConfig.baseUrl}/api/data/${clientConfig.apiVersion}`;
}

// =============================================================================
// Retry Configuration
// =============================================================================

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30_000;
/** Hard timeout per HTTP attempt. Without it a stalled connection can wedge
 *  the UI's React Query indefinitely; the BFF + Dataverse are usually well
 *  under 10s for normal queries. */
const REQUEST_TIMEOUT_MS = 30_000;

/** Status codes that are safe to retry (transient failures). */
const RETRYABLE_STATUSES = new Set([429, 502, 503, 504]);

/**
 * Fetch with automatic retry for transient errors.
 * Handles 429 (rate limit) with Retry-After header, and 502/503/504 with
 * exponential backoff. Also enforces a per-attempt timeout via AbortController
 * so a stalled network never hangs a React Query indefinitely.
 */
async function fetchWithRetry(url: string, init: RequestInit): Promise<Response> {
  let lastResponse: Response | undefined;
  let lastError: unknown;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const ctrl = new AbortController();
    // If the caller already passed a signal, abort us when theirs aborts too.
    const callerSignal = init.signal;
    const abortListener = callerSignal ? () => ctrl.abort(callerSignal.reason) : undefined;
    if (callerSignal && abortListener) {
      if (callerSignal.aborted) ctrl.abort(callerSignal.reason);
      else callerSignal.addEventListener('abort', abortListener, { once: true });
    }
    const timeoutId = setTimeout(() => ctrl.abort(new Error(`Dataverse request timeout after ${REQUEST_TIMEOUT_MS}ms`)), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(url, { ...init, signal: ctrl.signal });

      if (!RETRYABLE_STATUSES.has(response.status) || attempt === MAX_RETRIES) {
        return response;
      }

      lastResponse = response;

      // Respect Retry-After header (429 responses from Dataverse)
      const retryAfter = response.headers.get('Retry-After');
      const delayMs = retryAfter
        ? Math.min(parseFloat(retryAfter) * 1000, MAX_DELAY_MS)
        : Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS);

      console.warn(
        `Dataverse ${response.status} on attempt ${attempt + 1}/${MAX_RETRIES + 1}, retrying in ${Math.round(delayMs)}ms: ${url}`
      );
      await delay(delayMs);
    } catch (err) {
      // Network error (fetch itself threw) or our timeout aborted — retry,
      // but if the caller cancelled, propagate immediately.
      lastError = err;
      if (callerSignal?.aborted) throw err;
      if (attempt === MAX_RETRIES) break;

      const delayMs = Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS);
      console.warn(
        `Dataverse network error on attempt ${attempt + 1}/${MAX_RETRIES + 1}, retrying in ${delayMs}ms: ${url}`
      );
      await delay(delayMs);
    } finally {
      clearTimeout(timeoutId);
      if (callerSignal && abortListener) {
        callerSignal.removeEventListener('abort', abortListener);
      }
    }
  }

  // All retries exhausted — return last response or throw last error
  if (lastResponse) return lastResponse;
  throw lastError;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// =============================================================================
// Request Helpers
// =============================================================================

/**
 * Build request headers
 */
async function buildHeaders(options?: MutationOptions): Promise<Headers> {
  if (!clientConfig) {
    throw new Error('Dataverse client not initialized');
  }

  const token = await clientConfig.getToken();

  const headers = new Headers({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'OData-MaxVersion': '4.0',
    'OData-Version': '4.0',
  });

  // Impersonation: execute request as another user
  if (impersonateAsOid) {
    headers.set('CallerObjectId', impersonateAsOid);
  }

  // Return representation on create/update
  if (options?.returnRepresentation) {
    headers.set('Prefer', 'return=representation');
  }

  // Optimistic concurrency
  if (options?.ifMatch) {
    headers.set('If-Match', options.ifMatch);
  }

  // Only create if doesn't exist
  if (options?.ifNoneMatch) {
    headers.set('If-None-Match', options.ifNoneMatch);
  }

  // Caller-supplied extras (e.g. duplicate-detection enforcement)
  if (options?.extraHeaders) {
    for (const [k, v] of Object.entries(options.extraHeaders)) headers.set(k, v);
  }

  return headers;
}

/**
 * Handle API response
 */
async function handleResponse<T>(response: Response): Promise<T> {
  // No content (successful delete or update without return)
  if (response.status === 204) {
    return undefined as unknown as T;
  }

  const contentType = response.headers.get('content-type');
  const isJson = contentType?.includes('application/json');

  if (!response.ok) {
    if (isJson) {
      const error = await response.json() as ODataError;
      throw DataverseError.fromResponse(response.status, error);
    }
    throw new DataverseError(
      `Request failed: ${response.statusText}`,
      'REQUEST_FAILED',
      response.status
    );
  }

  if (isJson) {
    return response.json() as Promise<T>;
  }

  return undefined as unknown as T;
}

// =============================================================================
// CRUD Operations
// =============================================================================

/**
 * List entities with optional query parameters
 */
export async function list<T>(
  table: TableName | string,
  options: QueryOptions = {}
): Promise<ODataCollectionResponse<T>> {
  const queryString = buildQueryString(options);
  const url = `${getApiUrl()}/${table}${queryString}`;

  const headers = await buildHeaders();
  headers.set('Prefer', 'odata.include-annotations="OData.Community.Display.V1.FormattedValue,Microsoft.Dynamics.CRM.lookuplogicalname"');
  const response = await fetchWithRetry(url, { headers });

  return handleResponse<ODataCollectionResponse<T>>(response);
}

/**
 * List entities across pages, reporting whether the page cap cut the result
 * short. Callers that show the user a count or a date window need to know:
 * silently returning a partial set is how "last 30 days" came to mean "the
 * most recent N events" on the User Activity page (ADO #1558, #1305).
 */
export async function listPaged<T>(
  table: TableName | string,
  options: QueryOptions = {},
  maxPages = 50
): Promise<{ records: T[]; truncated: boolean }> {
  const allRecords: T[] = [];
  let page = 0;

  // First page uses the normal list() path
  const firstPage = await list<T>(table, options);
  allRecords.push(...firstPage.value);
  let nextLink = firstPage['@odata.nextLink'];

  // Follow @odata.nextLink for subsequent pages
  while (nextLink && ++page < maxPages) {
    const headers = await buildHeaders();
    headers.set('Prefer', 'odata.include-annotations="OData.Community.Display.V1.FormattedValue,Microsoft.Dynamics.CRM.lookuplogicalname"');
    const response = await fetchWithRetry(nextLink, { headers });
    const result = await handleResponse<ODataCollectionResponse<T>>(response);

    allRecords.push(...result.value);
    nextLink = result['@odata.nextLink'];
  }

  return { records: allRecords, truncated: Boolean(nextLink) };
}

/**
 * List all entities, automatically following @odata.nextLink pagination.
 * Use instead of list() when the result set may exceed 5000 rows.
 * Returns a flat array of all records across all pages.
 */
export async function listAll<T>(
  table: TableName | string,
  options: QueryOptions = {},
  maxPages = 50
): Promise<T[]> {
  const { records, truncated } = await listPaged<T>(table, options, maxPages);

  if (truncated) {
    console.warn(
      `listAll('${table}') hit max page limit (${maxPages}). ${records.length} records returned, more may exist.`
    );
  }

  return records;
}

/**
 * Get a single entity by ID
 */
export async function get<T>(
  table: TableName | string,
  id: string,
  options: QueryOptions = {}
): Promise<T> {
  const queryString = buildQueryString(options);
  const url = `${getApiUrl()}/${table}(${id})${queryString}`;

  const headers = await buildHeaders();
  headers.set('Prefer', 'odata.include-annotations="OData.Community.Display.V1.FormattedValue,Microsoft.Dynamics.CRM.lookuplogicalname"');
  const response = await fetchWithRetry(url, { headers });

  return handleResponse<T>(response);
}

/**
 * Create a new entity
 */
/**
 * Create a new entity.
 *
 * Overloads keep return types honest:
 *   - default / `returnRepresentation: true` → returns the full `T`
 *   - `returnRepresentation: false` → returns `{ id }` only (the BFF response
 *     is 204 + an `OData-EntityId` header containing the new GUID)
 *
 * Previously this signature returned `T` for both shapes via an unsafe
 * `as T` cast on the 204 path, leaving downstream consumers reading other
 * fields with an undefined-property runtime error.
 */
export async function create<T, TInput = Partial<T>>(
  table: TableName,
  data: TInput,
  options?: MutationOptions & { returnRepresentation?: true }
): Promise<T>;
export async function create<T, TInput = Partial<T>>(
  table: TableName,
  data: TInput,
  options: MutationOptions & { returnRepresentation: false }
): Promise<{ id: string }>;
export async function create<T, TInput = Partial<T>>(
  table: TableName,
  data: TInput,
  options: MutationOptions = { returnRepresentation: true }
): Promise<T | { id: string }> {
  const url = `${getApiUrl()}/${table}`;

  const headers = await buildHeaders(options);
  const response = await fetchWithRetry(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });

  // If returnRepresentation is false, extract ID from Location header.
  if (!options.returnRepresentation && response.status === 204) {
    const location = response.headers.get('OData-EntityId');
    if (location) {
      const idMatch = /\(([0-9a-f-]+)\)$/i.exec(location);
      if (idMatch?.[1]) {
        return { id: idMatch[1] };
      }
    }
    return { id: '' };
  }

  return handleResponse<T>(response);
}

/**
 * Update an existing entity (PATCH)
 */
export async function update<T, TInput = Partial<T>>(
  table: TableName,
  id: string,
  data: TInput,
  options: MutationOptions = {}
): Promise<T | void> {
  const url = `${getApiUrl()}/${table}(${id})`;

  const headers = await buildHeaders(options);
  const response = await fetchWithRetry(url, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(data),
  });

  return handleResponse<T>(response);
}

/**
 * Delete an entity (hard delete)
 */
export async function remove(
  table: TableName,
  id: string,
  options: MutationOptions = {}
): Promise<void> {
  const url = `${getApiUrl()}/${table}(${id})`;

  const headers = await buildHeaders(options);
  const response = await fetchWithRetry(url, {
    method: 'DELETE',
    headers,
  });

  await handleResponse<void>(response);
}

/**
 * Soft-delete an entity by setting statecode to Inactive (1).
 * The record remains in Dataverse but is excluded by active-only queries.
 */
export async function deactivate(
  table: TableName,
  id: string,
  options: MutationOptions = {}
): Promise<void> {
  await update(table, id, { statecode: 1, statuscode: 2 } as Record<string, unknown>, options);
}

// `upsert` was removed 2026-04-28. It was identical to `update` (PATCH against
// `/{table}({id})`), which IS upsert in Dataverse: PATCH on a non-existent
// GUID creates the row by default. If a future caller needs explicit
// "create only" or "update only" semantics, add an `If-None-Match: *` /
// `If-Match: *` header via MutationOptions rather than introducing a duplicate
// function.

// =============================================================================
// Batch Operations
// =============================================================================

interface BatchRequest {
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  url: string;
  body?: unknown;
  headers?: Record<string, string>;
}

/**
 * Execute multiple operations in a single request
 * Uses Dataverse $batch endpoint
 */
export async function batch(requests: BatchRequest[]): Promise<unknown[]> {
  const boundary = `batch_${crypto.randomUUID()}`;
  const url = `${getApiUrl()}/$batch`;

  const headers = await buildHeaders();
  headers.set('Content-Type', `multipart/mixed; boundary=${boundary}`);

  const body = buildBatchBody(requests, boundary);

  const response = await fetchWithRetry(url, {
    method: 'POST',
    headers,
    body,
  });

  if (!response.ok) {
    const error = await response.json() as ODataError;
    throw DataverseError.fromResponse(response.status, error);
  }

  // Parse multipart response
  const responseText = await response.text();
  return parseBatchResponse(responseText);
}

/**
 * Build multipart batch request body
 */
function buildBatchBody(requests: BatchRequest[], boundary: string): string {
  const parts: string[] = [];

  for (const req of requests) {
    parts.push(`--${boundary}`);
    parts.push('Content-Type: application/http');
    parts.push('Content-Transfer-Encoding: binary');
    parts.push('');
    parts.push(`${req.method} ${req.url} HTTP/1.1`);

    if (req.headers) {
      for (const [key, value] of Object.entries(req.headers)) {
        parts.push(`${key}: ${value}`);
      }
    }

    if (req.body) {
      parts.push('Content-Type: application/json');
      parts.push('');
      parts.push(JSON.stringify(req.body));
    } else {
      parts.push('');
    }
  }

  parts.push(`--${boundary}--`);
  return parts.join('\r\n');
}

/**
 * Parse a Dataverse $batch multipart/mixed response.
 *
 * For each non-empty part:
 *   1. Extract the inner HTTP response status line (e.g. `HTTP/1.1 204 No Content`).
 *   2. If the status is 4xx/5xx, throw — partial failures must not be hidden.
 *   3. If the part has a JSON body (`{...}`), parse and push it.
 *   4. Empty bodies (204s) push `null` so the result array is positional.
 *
 * Replaces a regex-only approach that silently swallowed every non-JSON part
 * and ignored per-part status codes, which meant a failed PATCH inside a
 * batch read as a successful no-op.
 */
function parseBatchResponse(responseText: string): unknown[] {
  const results: unknown[] = [];

  // Split on the response boundary. Dataverse picks its own boundary token
  // (independent of the request boundary). The terminator is `--batchresponse_…--`.
  const parts = responseText
    .split(/--batchresponse_[a-z0-9-]+(?:--)?/i)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  for (const part of parts) {
    // Find the inner HTTP response status line. Format: `HTTP/1.1 <code> <reason>`.
    const statusMatch = /^HTTP\/[\d.]+\s+(\d{3})\s+([^\r\n]*)/m.exec(part);
    if (!statusMatch) {
      // Not an HTTP response part — could be a Content-Type preamble. Skip.
      continue;
    }
    const status = parseInt(statusMatch[1]!, 10);
    const reason = statusMatch[2]!;

    if (status >= 400) {
      // Try to surface the inner error body if Dataverse included one.
      const bodyMatch = /\r?\n\r?\n([\s\S]*)$/.exec(part);
      const innerBody = bodyMatch?.[1]?.trim() ?? '';
      let detail = '';
      try {
        const parsed = JSON.parse(innerBody) as { error?: { message?: string } };
        detail = parsed?.error?.message ? `: ${parsed.error.message}` : '';
      } catch {
        if (innerBody) detail = `: ${innerBody.slice(0, 200)}`;
      }
      throw new Error(`Batch sub-request failed (${status} ${reason})${detail}`);
    }

    // Locate the body — first blank line after the inner HTTP headers.
    // We want everything after the SECOND blank line (first separates the
    // outer multipart headers from the inner HTTP response, second separates
    // inner HTTP headers from inner body).
    const bodyMatch = /\r?\n\r?\n([\s\S]*?)\r?\n\r?\n([\s\S]*)/.exec(part);
    const body = bodyMatch?.[2]?.trim() ?? '';
    if (!body) {
      results.push(null);
      continue;
    }

    try {
      results.push(JSON.parse(body));
    } catch {
      // Body was non-empty but not JSON (rare for Dataverse) — push raw text.
      results.push(body);
    }
  }

  return results;
}

/** Per-sub-request outcome from batchSettled. Positional with the input. */
export interface BatchSettledResult {
  ok: boolean;
  status: number;
  /** Parsed JSON body when the sub-request returned one, else null. */
  body: unknown;
  /** Dataverse error message when ok is false. */
  error?: string;
}

/**
 * Execute multiple operations in one $batch request WITHOUT failing the whole
 * call on one bad sub-request. Sends `Prefer: odata.continue-on-error` so
 * Dataverse processes every part; outcomes come back positionally, one per
 * request, and the caller owns per-item error handling. Use this for bulk
 * seeding where one bad record must not abort the rest; use `batch` when any
 * failure should throw.
 *
 * Unlike `batch`, request `url`s here are relative to the API root (e.g.
 * `vba_groupsolutions`), so callers outside this module can build them.
 */
export async function batchSettled(requests: BatchRequest[]): Promise<BatchSettledResult[]> {
  if (requests.length === 0) return [];

  const boundary = `batch_${crypto.randomUUID()}`;
  const apiUrl = getApiUrl();
  const absolute = requests.map((req) => ({
    ...req,
    url: /^https?:/i.test(req.url) ? req.url : `${apiUrl}/${req.url}`,
  }));

  const headers = await buildHeaders();
  headers.set('Content-Type', `multipart/mixed; boundary=${boundary}`);
  headers.set('Prefer', 'odata.continue-on-error');

  const response = await fetchWithRetry(`${apiUrl}/$batch`, {
    method: 'POST',
    headers,
    body: buildBatchBody(absolute, boundary),
  });

  if (!response.ok) {
    const error = await response.json() as ODataError;
    throw DataverseError.fromResponse(response.status, error);
  }

  const parts = splitBatchParts(await response.text());
  // Positional mapping is the whole contract; a count mismatch means we would
  // attribute results to the wrong records, so refuse rather than guess.
  if (parts.length !== requests.length) {
    throw new Error(
      `Batch response had ${parts.length} parts for ${requests.length} requests; cannot map results positionally`
    );
  }

  return parts.map((part) => {
    let body: unknown = null;
    if (part.bodyText) {
      try {
        body = JSON.parse(part.bodyText);
      } catch {
        body = part.bodyText;
      }
    }
    if (part.status >= 400) {
      const message = (body as { error?: { message?: string } } | null)?.error?.message;
      return {
        ok: false,
        status: part.status,
        body,
        error: message ?? `HTTP ${part.status} ${part.reason}`.trim(),
      };
    }
    return { ok: true, status: part.status, body };
  });
}

/**
 * Split a $batch multipart response into per-part status + raw body text.
 * Part structure: outer multipart headers, blank line, inner HTTP status line
 * + headers, blank line, body. The second blank line therefore fronts the body.
 */
function splitBatchParts(responseText: string): Array<{ status: number; reason: string; bodyText: string }> {
  const parts = responseText
    .split(/--batchresponse_[a-z0-9-]+(?:--)?/i)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  const out: Array<{ status: number; reason: string; bodyText: string }> = [];
  for (const part of parts) {
    const statusMatch = /^HTTP\/[\d.]+\s+(\d{3})\s+([^\r\n]*)/m.exec(part);
    if (!statusMatch) continue; // Content-Type preamble, not an HTTP part
    const bodyMatch = /\r?\n\r?\n([\s\S]*?)\r?\n\r?\n([\s\S]*)/.exec(part);
    out.push({
      status: parseInt(statusMatch[1]!, 10),
      reason: statusMatch[2]!,
      bodyText: bodyMatch?.[2]?.trim() ?? '',
    });
  }
  return out;
}

// =============================================================================
// Association Operations
// =============================================================================

/**
 * Associate two entities (create relationship)
 */
export async function associate(
  table: TableName,
  id: string,
  navigationProperty: string,
  relatedTable: TableName,
  relatedId: string
): Promise<void> {
  const url = `${getApiUrl()}/${table}(${id})/${navigationProperty}/$ref`;

  const headers = await buildHeaders();
  const response = await fetchWithRetry(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      '@odata.id': `${getApiUrl()}/${relatedTable}(${relatedId})`,
    }),
  });

  await handleResponse<void>(response);
}

/**
 * Disassociate two entities (remove relationship)
 */
export async function disassociate(
  table: TableName,
  id: string,
  navigationProperty: string,
  relatedId?: string
): Promise<void> {
  // For collection-valued navigation properties (N:N), the OData spec wants
  // the related key in the path: /<table>(<id>)/<navProp>(<relatedId>)/$ref.
  // For single-valued (1:N), no relatedId is needed.
  const url = relatedId
    ? `${getApiUrl()}/${table}(${id})/${navigationProperty}(${relatedId})/$ref`
    : `${getApiUrl()}/${table}(${id})/${navigationProperty}/$ref`;

  const headers = await buildHeaders();
  const response = await fetchWithRetry(url, {
    method: 'DELETE',
    headers,
  });

  await handleResponse<void>(response);
}

// =============================================================================
// Raw Fetch (for metadata and non-standard endpoints)
// =============================================================================

/**
 * Fetch a raw URL path relative to the API base.
 * Used for metadata queries and other non-standard endpoints.
 */
export async function fetchRaw<T>(path: string): Promise<T> {
  const url = `${getApiUrl()}/${path}`;
  const headers = await buildHeaders();
  const response = await fetchWithRetry(url, { headers });
  return handleResponse<T>(response);
}

/**
 * Run a FetchXML query against a table's entity set and return the rows.
 *
 * Use this for SELECT DISTINCT / aggregation that OData query options can't
 * express — e.g. distinct (solution, serviceGroup, service) facets for a
 * filter dropdown, instead of pulling every enrollment row and de-duping in
 * the client. `table` is the entity SET name (plural); the FetchXML's own
 * `<entity name="...">` carries the logical (singular) name.
 *
 * Linked-entity attributes come back aliased as `"<alias>.<attr>"`.
 */
export async function fetchXml<T>(table: TableName | string, xml: string): Promise<T[]> {
  const res = await fetchRaw<ODataCollectionResponse<T>>(
    `${table}?fetchXml=${encodeURIComponent(xml)}`,
  );
  return res.value;
}

/**
 * Execute a Dataverse bound or unbound action (e.g. AddMembersTeam).
 * Actions live under their verb-name at the API root and take a JSON body.
 */
export async function executeAction<T = void>(
  actionName: string,
  body?: Record<string, unknown>,
): Promise<T> {
  const url = `${getApiUrl()}/${actionName}`;
  const headers = await buildHeaders();
  headers.set('Content-Type', 'application/json');
  const response = await fetchWithRetry(url, {
    method: 'POST',
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  return handleResponse<T>(response);
}

// =============================================================================
// Exported Client Object
// =============================================================================

export const dataverseClient = {
  initialize: initializeDataverseClient,
  list,
  listAll,
  listPaged,
  get,
  create,
  update,
  remove,
  deactivate,
  batch,
  batchSettled,
  associate,
  disassociate,
  fetchRaw,
  fetchXml,
  executeAction,
};
